import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { shownCartData } from 'src/components/cart/amazonRecordPrices';
import { AMAZON_ITEM_RECHECK_MS } from 'src/components/deals/amazonItemFilters';
import type { CartGetData } from 'src/types';
import { useGetCartAPI, useNow } from '.';

type Props = {
  cartId: string;
};

export const useGetCartQuery = ({ cartId }: Props) => {
  const nav = useNavigate();

  const getCartAPI = useGetCartAPI();
  const now = useNow(AMAZON_ITEM_RECHECK_MS);
  // The cart is not refetched, so an Amazon record price left on screen falls back to its saved price on the clock.
  const showCart = useCallback((data: CartGetData) => shownCartData(data, now), [now]);

  const cartQuery = useQuery({
    queryKey: ['cart', cartId],
    queryFn: () => getCartAPI(cartId),
    select: showCart,
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: 0,
    enabled: !!cartId,
  });

  useEffect(() => {
    if (
      cartQuery.isError ||
      ((cartQuery.data === undefined || cartQuery.data === null) &&
        !cartQuery.isLoading &&
        cartQuery.isEnabled)
    ) {
      nav('/get', { state: { error: true, cartId } });
    }
  }, [
    cartQuery.isError,
    cartId,
    nav,
    cartQuery.data,
    cartQuery.isLoading,
    cartQuery.isEnabled,
  ]);

  return cartQuery;
};
